import React, { useEffect, useId, useRef } from "react";
import { Button, Popover, PopoverAnchor, PopoverContent } from "@mindstonehq/ui";
import { ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { Trans } from "@lingui/react/macro";

import type { SpaceTransformationDashboard } from "~/utils/transformation-dashboard";

// FOX-6291 A5: the first plain click on the Transformation Engine sidebar item
// opens this panel instead of the new tab, once per person (the seen flag is on
// the user's state store). Melissanthi's "dismissable tooltip": a small panel
// pointing at the item, interactive, so a Popover rather than a Tooltip.

export type EngineIntroAudience = "leader" | "champion" | "learner";
export type EngineIntroChoice = "open" | "not_now";

/**
 * Leader copy for admins and the engine's buyers, champion copy for its
 * champions, learner copy for its learners (plan 261104_learner-dashboard-link;
 * wording: apps/learning/docs/copy-ab/261104_engine-first-visit-learner.md).
 */
export function engineIntroAudience(dashboard: SpaceTransformationDashboard): EngineIntroAudience {
  if (dashboard.isAdmin || dashboard.audience === "buyer") return "leader";
  return dashboard.audience === "champion" ? "champion" : "learner";
}

export function EngineIntroPanel({
  id,
  open,
  href,
  audience,
  side,
  onDismiss,
  children,
}: React.PropsWithChildren<{
  /** The panel's element id, for the item's aria-controls. */
  id?: string;
  open: boolean;
  href: string;
  audience: EngineIntroAudience;
  side: "right" | "bottom";
  /** Called for Open, Not now, Escape and a click outside. */
  onDismiss: (choice: EngineIntroChoice) => void;
}>) {
  const titleId = useId();
  const bodyId = useId();
  // A click outside closes the panel without pulling focus back to the item,
  // so the click lands where the person meant it.
  const closedOutside = useRef(false);
  const openLink = useRef<HTMLAnchorElement>(null);
  const anchor = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(open);
  useEffect(() => {
    // There is no Radix trigger to return to: when the panel closes, put
    // focus back on the item itself, unless the person clicked elsewhere.
    if (wasOpen.current && !open && !closedOutside.current) {
      anchor.current?.querySelector<HTMLElement>("a, button")?.focus();
    }
    if (!open) closedOutside.current = false;
    wasOpen.current = open;
  }, [open]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss("not_now");
      }}
    >
      <PopoverAnchor asChild>
        <div ref={anchor}>{children}</div>
      </PopoverAnchor>
      <PopoverContent
        id={id}
        side={side}
        align="start"
        // Wide enough for Open and Not now on one row; never wider than a phone.
        className="w-96 max-w-[calc(100vw-2rem)]"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onOpenAutoFocus={(event) => {
          // Start on the main action rather than the first focusable Radix finds.
          event.preventDefault();
          openLink.current?.focus();
        }}
        onInteractOutside={() => {
          closedOutside.current = true;
        }}
        // Focus on close is handled above.
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <p id={titleId} className="font-semibold text-base text-foreground">
          <Trans>Transformation Engine</Trans>
        </p>
        <p id={bodyId} className="mt-1 text-sm text-muted-foreground">
          {audience === "leader" ? (
            <Trans>
              The Transformation Engine opens in a new tab. It shows how your company is adopting AI
              and what to focus on next. Inviting, emailing and exporting your cohorts now happens in
              its Academy.
            </Trans>
          ) : audience === "champion" ? (
            <Trans>
              The Transformation Engine opens in a new tab. It shows how your team or cohort is getting
              on and who could use your support. Lessons stay here in Mindstone Learning.
            </Trans>
          ) : (
            <Trans>
              The Transformation Engine opens in a new tab. Check your progress using AI at work and
              whether you have cohort work to finish. Lessons stay here in Mindstone Learning.
            </Trans>
          )}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button asChild size="sm">
            <a ref={openLink} href={href} target="_blank" rel="noopener" onClick={() => onDismiss("open")}>
              <Trans>Open the Transformation Engine</Trans>
              <ArrowUpRightIcon className="size-4" aria-hidden="true" />
              <span className="sr-only">
                <Trans>(opens in new tab)</Trans>
              </span>
            </a>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDismiss("not_now")}>
            <Trans>Not now</Trans>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
